import { Mail, MailX } from "lucide-react";

import { Button } from "@/components/ui/button";
import ACL_RELATIONSHIPS from "acl-relationships";
import EmailHtmlFrame from "components/email/EmailHtmlFrame";
import withRoleCheck from "components/hocs/withRoleCheck";
import _ViewEmailModal from "components/modals/ViewEmailModal";
import CreateNoteButton from "components/LogItem/elements/CreateNoteButton";
import EntryDetails from "components/LogItem/elements/EntryDetails";
import ResendEmailButton from "components/LogItem/elements/ResendEmailButton";
import TextContentDialog from "components/LogItem/elements/TextContentDialog";
import { basicDateFormatter } from "components/LogItem/messages/dates";
import { getModelLink } from "components/LogItem/messages/modelLinks";

const ViewEmailModal = withRoleCheck(_ViewEmailModal, [
  ACL_RELATIONSHIPS.campaignTargetEmail.read,
]);

const contactLinkFor = ({ contact_id, contact_name }) =>
  getModelLink(contact_id, "Contact", { label: contact_name });

const theEmail = subject => (subject ? `the email "${subject}"` : "an email");

export const EMAIL_EVENTS = {
  delivered: {
    title: "Email Sent",
    icon: Mail,
    message: item => {
      const {
        email_subject,
        user_name,
        info: {
          is_manual,
          subject,
          email,
          date,
          from_email,
          comments,
          campaign_target_email_id,
        },
      } = item;
      const contactLink = contactLinkFor(item);
      const emailSubject = email_subject || subject;

      if (is_manual) {
        return (
          <>
            <p>
              {user_name} sent an email to {email}.
            </p>
            <EntryDetails
              rows={[
                { label: "Subject", value: emailSubject },
                { label: "Contact", value: contactLink },
                { label: "Date", value: basicDateFormatter(date) },
                { label: "From", value: from_email },
                { label: "Email ID", value: campaign_target_email_id },
                { label: "Comments", value: comments },
              ]}
            />
          </>
        );
      }

      return (
        <>
          {user_name ? (
            <p>
              {user_name} sent {theEmail(emailSubject)} to {contactLink}.
            </p>
          ) : (
            <p>
              {contactLink} was sent {theEmail(emailSubject)}.
            </p>
          )}
          <EntryDetails
            rows={[
              { label: "Email ID", value: campaign_target_email_id },
              { label: "Comments", value: comments },
            ]}
          />
        </>
      );
    },
    actions: ({
      contact_id,
      auditable_id,
      modelType,
      modelId,
      logId,
      email,
      item,
      campaign_id,
      entity_id,
    }) => {
      const { info } = item;

      return (
        <>
          <ViewEmailModal
            emailId={info.campaign_target_email_id}
            emailUrl={info.s3_info?.url}
            campaignId={campaign_id}
            entityId={entity_id}
          />
          <CreateNoteButton
            modelType={modelType}
            modelId={modelId}
            logId={logId}
            label="Create Note"
          />
          {!info.is_manual && (
            <ResendEmailButton
              contactId={contact_id}
              campaignTargetEmailId={auditable_id}
              disabled={!email}
            />
          )}
          {info.s3_info?.url && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => window.open(info.s3_info.url, "_blank")}
            >
              Download EML
            </Button>
          )}
        </>
      );
    },
  },
  opened: {
    title: "Email Opened",
    icon: Mail,
    message: item => (
      <p>
        {contactLinkFor(item)} opened {theEmail(item.email_subject)}.
      </p>
    ),
  },
  clicked: {
    title: "Email Link Clicked",
    icon: Mail,
    message: item => (
      <p>
        {contactLinkFor(item)} clicked {item.info.url} in{" "}
        {theEmail(item.email_subject)}.
      </p>
    ),
  },
  errored: {
    title: "Email Failed",
    icon: MailX,
    // A CC or BCC failure carries the copy's address and kind flat in info,
    // alongside the fields every failure has.
    message: item => {
      const { error_msg, bounce_description, to_addr, email_type } = item.info;
      return (
        <>
          {to_addr ? (
            <p>
              The {email_type?.toUpperCase()} copy to {to_addr} failed:{" "}
              {error_msg}
            </p>
          ) : (
            <p>
              The email to {contactLinkFor(item)} failed: {error_msg}
            </p>
          )}
          <EntryDetails
            rows={[{ label: "Reason", value: bounce_description }]}
          />
        </>
      );
    },
  },
  cc_tracking: {
    title: "Email Tracked (CC)",
    icon: Mail,
    message: item => {
      const { email_data } = item.info;
      return (
        <>
          <p>
            {email_data.sender} sent {theEmail(email_data.subject)} to{" "}
            {contactLinkFor(item)}.
          </p>
          <EntryDetails rows={[{ label: "To", value: email_data.To }]} />
        </>
      );
    },
    actions: ({
      modelType,
      modelId,
      logId,
      item: {
        info: { email_data },
      },
    }) => (
      <>
        <TextContentDialog label="View Email Sent" title="Email Content" wide>
          <EmailHtmlFrame
            html={email_data["stripped-html"]}
            title="Email content"
          />
        </TextContentDialog>
        <CreateNoteButton
          modelType={modelType}
          modelId={modelId}
          logId={logId}
        />
      </>
    ),
  },
  // The legacy action for a sent email, which older rows still carry.
  email: {
    title: "Email Sent",
    icon: Mail,
    message: item => <p>An email was sent to {contactLinkFor(item)}.</p>,
    actions: ({
      item: {
        info: { contents },
      },
    }) => (
      <TextContentDialog
        label="View EML Contents"
        title="Email Contents"
        disabled={!contents}
      >
        {contents}
      </TextContentDialog>
    ),
  },
};
